import * as React from "react";
import type { ColumnOrderState } from "@tanstack/react-table";

const STORAGE_PREFIX = "table-v2:col-order:";
const getStorageKey = (tableKey: string) => `${STORAGE_PREFIX}${tableKey}`;

const readInitial = (tableKey: string | undefined): ColumnOrderState => {
  if (!tableKey || typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(getStorageKey(tableKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed as ColumnOrderState;
    }
    return [];
  } catch {
    return [];
  }
};

export const useColumnOrderPersistence = (
  tableKey: string | undefined,
  enabled: boolean,
  initialOrder?: ColumnOrderState,
) => {
  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(
    () => {
      if (!enabled) return [];
      if (initialOrder && initialOrder.length > 0) return initialOrder;
      return readInitial(tableKey);
    },
  );

  // Sync state when API-provided initialOrder arrives after mount
  const prevInitialOrder = React.useRef(initialOrder);
  React.useEffect(() => {
    if (
      enabled &&
      initialOrder &&
      initialOrder.length > 0 &&
      prevInitialOrder.current !== initialOrder
    ) {
      prevInitialOrder.current = initialOrder;
      setColumnOrder(initialOrder);
    }
  }, [initialOrder, enabled]);

  React.useEffect(() => {
    if (!enabled || !tableKey || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        getStorageKey(tableKey),
        JSON.stringify(columnOrder),
      );
    } catch {
      // quota / private mode — silently ignore
    }
  }, [columnOrder, enabled, tableKey]);

  return [columnOrder, setColumnOrder] as const;
};
